import { CLUSTER_ITEMS } from "@/constants/cluster";
import env from "@/env";
import {
  Cluster,
  ClusterType,
  ContainerHardware,
  VirtualMachineHardware,
  DynamicSupplyHardware
} from "@/types";
import { createContext } from "react";

export const getMaxDuration = (clusterType: ClusterType, durationType: string) => {
  switch (clusterType) {
    case ClusterType.KUBERNETES:
      switch (durationType) {
        case "daily":
          return 14;
        case "weekly":
          return 2;
      }
      break;
    case ClusterType.BARE_METAL:
      switch (durationType) {
        case "hourly":
          return 365 * 24;
        case "daily":
          return 365;
        case "weekly":
          return 52;
        case "monthly":
          return 12;
      }
      break;
    default:
      switch (durationType) {
        case "hourly":
          return 60 * 24 * 30 * 4;
        case "daily":
          return 30 * 4;
        case "weekly":
          return 4;
      }
      break;
  }

  return Number.POSITIVE_INFINITY;
};

export const getDurationQuantity = (
  newDurationType: string,
  oldDurationType: string,
  oldDurationQuantity: number | undefined
) => {
  if (typeof oldDurationQuantity !== "number") {
    return;
  }

  const oldDurationQuantityInHours = getDurationQuantityInHours(
    oldDurationQuantity,
    oldDurationType
  );
  if (typeof oldDurationQuantityInHours !== "number") {
    return;
  }

  switch (newDurationType) {
    case "hourly":
      return oldDurationQuantityInHours || 1;
    case "daily":
      return Math.round(oldDurationQuantityInHours / 24) || 1;
    case "weekly":
      return Math.round(oldDurationQuantityInHours / (24 * 7)) || 1;
    case "monthly":
      return Math.round(oldDurationQuantityInHours / (24 * 7 * 4)) || 1;
  }
};

export const getDurationQuantityInHours = (durationQuantity: number, durationType: string) => {
  switch (durationType) {
    case "hourly":
      return durationQuantity;
    case "daily":
      return durationQuantity * 24;
    case "weekly":
      return durationQuantity * 24 * 7;
    case "monthly":
      return durationQuantity * 24 * 7 * 4;
  }
};

export const getMinDuration = (clusterType: ClusterType, durationType: string) => {
  switch (clusterType) {
    case ClusterType.KUBERNETES:
      switch (durationType) {
        case "daily":
          return 1;
        case "weekly":
          return 1;
        case "monthly":
          return 1;
      }
  }

  switch (clusterType) {
    case ClusterType.BARE_METAL:
      switch (durationType) {
        case "hourly":
          return env.CLUSTER_CREATION_BARE_METAL_HOURLY_MIN_DURATION;
        case "daily":
          return env.CLUSTER_CREATION_BARE_METAL_DAILY_MIN_DURATION;
        case "weekly":
          return 1;
        case "monthly":
          return 1;
      }
  }

  switch (clusterType) {
    case ClusterType.BARE_METAL_REQUEST:
      switch (durationType) {
        case "hourly":
          return env.CLUSTER_CREATION_BARE_METAL_HOURLY_MIN_DURATION;
        case "daily":
          return env.CLUSTER_CREATION_BARE_METAL_DAILY_MIN_DURATION;
        case "weekly":
          return 1;
        case "monthly":
          return 1;
      }
  }

  return 1;
};

export const getClusterTypeFromUrl = (url: string) => {
  return CLUSTER_ITEMS.find((item) => {
    return item.url === url;
  })?.value as ClusterType;
};

export const getMaxHardware = (clusterType: ClusterType) => {
  switch (clusterType) {
    case ClusterType.RAY:
      return env.CLUSTER_CREATION_RAY_MAX_HARDWARE;
    case ClusterType.BARE_METAL:
      return env.CLUSTER_CREATION_BARE_METAL_MAX_HARDWARE;
    case ClusterType.KUBERNETES:
      return env.CLUSTER_CREATION_KUBERNETES_MAX_HARDWARE;
    case ClusterType.CONTAINER:
      return env.CLUSTER_CREATION_CONTAINER_MAX_HARDWARE;
  }

  return Number.POSITIVE_INFINITY;
};

export const getMinHardware = (clusterType: ClusterType) => {
  switch (clusterType) {
    case ClusterType.RAY:
      return env.CLUSTER_CREATION_RAY_MIN_HARDWARE;
    case ClusterType.BARE_METAL:
      return env.CLUSTER_CREATION_BARE_METAL_MIN_HARDWARE;
    case ClusterType.KUBERNETES:
      return env.CLUSTER_CREATION_KUBERNETES_MIN_HARDWARE;
    case ClusterType.CONTAINER:
      return env.CLUSTER_CREATION_CONTAINER_MIN_HARDWARE;
  }

  return 1;
};

export const ClusterResultsDataTableContext = createContext<{
  setSelectedCluster: React.Dispatch<React.SetStateAction<Cluster | undefined>>;
  setSelectedAction: React.Dispatch<React.SetStateAction<string | undefined>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}>(undefined as any);

export const PERSISTED_FIELDS = [
  "hardware",
  "hardwareInfo",
  "hardwareQuantity",
  "hardwareQty",
  "clusterType",
  "supplier",
  "securityCompliance",
  "location",
  "locations",
  "connectivityTier",
  "hardware",
  "service",
  "masterNode",
  "durationType",
  "durationQuantity",
  "containerImage",
  "isPrivate",
  "username",
  "secret",
  "trafficPort",
  "command",
  "replicaCount",
  "currency",
  "sshType",
  "vmImageType",
  "githubIds",
  "sshKeys",
  "selectedSshKeyIds",
  "networkServices",
  "replicaCount",
  "supplier",
  "deploymentId",
  "isConfidentialCompute",
  "containerRecipe",
  "gpuSearch",
  "gpuFilters",
  "gpuShowUnavailable",
  "gpuSort"
];

export const PERSISTED_RAY_CLUSTER_KEY = "RAY_CLUSTER_STATE";

export const PERSISTED_BARE_METAL_CLUSTER_KEY = "BARE_METAL_CLUSTER_STATE";

export const PERSISTED_CONTAINER_CLUSTER_KEY = "CONTAINER_CLUSTER_STATE";

export const PERSISTED_KUBERNETES_CLUSTER_KEY = "KUBERNETES_CLUSTER_STATE";

export const PERSISTED_VM_CLUSTER_KEY = "VM_CLUSTER_STATE";

/** Same-tab flag: draft may be restored across refresh / OAuth, not after SPA leave-and-return. */
export const CLUSTER_DRAFT_SESSION_KEY = "CLUSTER_DRAFT_SESSION_ACTIVE";

const CREATE_CLUSTER_PATH_STORAGE_KEYS: Record<string, string> = {
  "/cloud/container/create-cluster": PERSISTED_CONTAINER_CLUSTER_KEY,
  "/cloud/virtual-machines/create-cluster": PERSISTED_VM_CLUSTER_KEY,
  "/cloud/kubernetes/create-cluster": PERSISTED_KUBERNETES_CLUSTER_KEY
};

export const getPersistedClusterKey = (clusterType: ClusterType) => {
  switch (clusterType) {
    case ClusterType.RAY:
      return PERSISTED_RAY_CLUSTER_KEY;
    case ClusterType.VMAAS:
      return PERSISTED_VM_CLUSTER_KEY;
    case ClusterType.CONTAINER:
      return PERSISTED_CONTAINER_CLUSTER_KEY;
    case ClusterType.BARE_METAL:
    case ClusterType.BARE_METAL_REQUEST:
      return PERSISTED_BARE_METAL_CLUSTER_KEY;
    case ClusterType.KUBERNETES:
      return PERSISTED_KUBERNETES_CLUSTER_KEY;
    default:
      return undefined;
  }
};

export const getCreateClusterPathname = (path: string) => path.split("?")[0];

export const isCreateClusterPath = (path: string) => {
  return getCreateClusterPathname(path) in CREATE_CLUSTER_PATH_STORAGE_KEYS;
};

export const getPersistedStorageKeyForCreatePath = (path: string) => {
  return CREATE_CLUSTER_PATH_STORAGE_KEYS[getCreateClusterPathname(path)];
};

export const markClusterDraftSession = (persistedStorageKey: string) => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(CLUSTER_DRAFT_SESSION_KEY, persistedStorageKey);
};

export const hasClusterDraftSession = (persistedStorageKey: string) => {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(CLUSTER_DRAFT_SESSION_KEY) === persistedStorageKey;
};

export const clearClusterDraftSession = () => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(CLUSTER_DRAFT_SESSION_KEY);
};

export const clearPersistedClusterDraft = (key: string) => {
  localStorage.removeItem(key);
  if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(CLUSTER_DRAFT_SESSION_KEY) === key) {
    clearClusterDraftSession();
  }
};

/** Writes form draft to localStorage and marks this tab's create session for restore. */
export const persistClusterDraft = (key: string, data: Record<string, unknown>) => {
  localStorage.setItem(key, JSON.stringify(data));
  markClusterDraftSession(key);
};

export const getPersistedExpiration = () => {
  return new Date().getTime() + 1000 * 60 * 60;
};

export const getPersistedClusterState = (key: string) => {
  const persistedStateString = localStorage.getItem(key);
  if (!persistedStateString) {
    return;
  }

  const persistedState = JSON.parse(persistedStateString);
  if (!persistedState) {
    return;
  }
  if (
    typeof persistedState.expires !== "number" ||
    persistedState.expires <= new Date().getTime()
  ) {
    localStorage.removeItem(key);
    return;
  }

  return persistedState;
};

export const getFlashSaleGpus = () => {
  const flashSaleGpus = env.FLASH_SALE_GPUS;
  return flashSaleGpus.map((str) => {
    const [gpu, price] = str.split(":");
    return {
      gpu: gpu.trim(),
      price: price.trim()
    };
  });
};

// Normalize a GPU identifier to a canonical key for matching.
// Examples:
//  - "GeForce RTX 4090" -> "4090"
//  - "RTX4090" -> "4090"
//  - "H100 80GB" -> "h100"
//  - "L40S" -> "l40s"
//  - "A4000 16GB" -> "a4000"
//  - "GH200 96GB" -> "gh200"
const normalizeGpuIdentifier = (raw: string) => {
  const s = (raw || "").toLowerCase();

  const rtx = s.match(/rtx\s*-?\s*(\d{4})/i);
  if (rtx) return rtx[1];

  const combo = s.match(/\b(gh|h|a|l|v|t|b)\s*-?\s*(\d{1,4})(s?)\b/i);
  if (combo) return `${combo[1]}${combo[2]}${combo[3]}`;

  const fourDigits = s.match(/\b(\d{4})\b/);
  if (fourDigits) return fourDigits[1];

  return s.replace(/[^a-z0-9]/g, "");
};

// Extract all plausible GPU keys from a label for matching against env keys.
const extractGpuKeysFromLabel = (label: string) => {
  const s = (label || "").toLowerCase();
  const keys = new Set<string>();

  // RTX series (e.g., RTX 4090)
  for (const m of s.matchAll(/rtx\s*-?\s*(\d{4})/g)) {
    keys.add(m[1]);
  }

  // Common letter+digits combos (h100, a4000, l40s, gh200, v100, t4, b200, etc.)
  for (const m of s.matchAll(/\b(gh|h|a|l|v|t|b)\s*-?\s*(\d{1,4})(s?)\b/g)) {
    keys.add(`${m[1]}${m[2]}${m[3]}`);
  }

  // Bare 4-digit models (e.g., 4090)
  for (const m of s.matchAll(/\b(\d{4})\b/g)) {
    keys.add(m[1]);
  }

  return keys;
};

export const isFlashSaleGpu = (gpuName: string) => {
  const flashSaleGpus = getFlashSaleGpus();
  const labelKeys = extractGpuKeysFromLabel(gpuName);

  if (labelKeys.size) {
    const envKeys = flashSaleGpus.map((f) => normalizeGpuIdentifier(f.gpu));
    for (const key of labelKeys) {
      if (envKeys.includes(key)) return true;
    }
    return false;
  }

  // Fallback: substring match on normalized strings
  const normLabel = normalizeGpuIdentifier(gpuName);
  return flashSaleGpus.some((f) => {
    const envKey = normalizeGpuIdentifier(f.gpu);
    return normLabel.includes(envKey) || envKey.includes(normLabel);
  });
};

export const getFlashSaleGpuPrice = (gpuName: string) => {
  const flashSaleGpus = getFlashSaleGpus();
  const labelKeys = extractGpuKeysFromLabel(gpuName);
  if (labelKeys.size) {
    for (const f of flashSaleGpus) {
      const envKey = normalizeGpuIdentifier(f.gpu);
      if (labelKeys.has(envKey)) return parseFloat(f.price ?? "0");
    }
    return undefined;
  }

  // Fallback: substring match on normalized strings
  const normLabel = normalizeGpuIdentifier(gpuName);
  for (const f of flashSaleGpus) {
    const envKey = normalizeGpuIdentifier(f.gpu);
    if (normLabel.includes(envKey) || envKey.includes(normLabel)) return f.price;
  }
  return undefined;
};

export const findGpuByName = (
  gpuName: string,
  gpus: (VirtualMachineHardware | ContainerHardware)[]
) => {
  for (const f of gpus) {
    if (f.name.toLowerCase().includes(decodeURIComponent(gpuName.toLowerCase()))) return f;
  }
  return undefined;
};

export const findDynamicSupplyGpuByName = (gpuName: string, gpus: DynamicSupplyHardware[]) => {
  for (const f of gpus) {
    if (f.hardwareName.toLowerCase().includes(decodeURIComponent(gpuName.toLowerCase()))) return f;
  }
  return undefined;
};

/**
 * Maps ClusterType enum values to human-readable strings for HubSpot
 */
export const getClusterTypeDisplayName = (clusterType: ClusterType | string): string => {
  const mapping: Record<string, string> = {
    [ClusterType.VMAAS]: "Virtual Machine",
    [ClusterType.KUBERNETES]: "Kubernetes",
    [ClusterType.CONTAINER]: "Container",
    [ClusterType.BARE_METAL]: "Bare Metal",
    [ClusterType.CONFIDENTIAL_COMPUTE]: "Confidential Compute",
    [ClusterType.RAY]: "Ray"
  };
  return mapping[clusterType] || clusterType;
};

export const submitHardwareRequestToHubSpot = async (data: {
  regions: string[];
  hardwareName: string;
  confidentialCompute: boolean;
  numberOfGpus: number;
  contactEmail: string;
  clusterType: string; // Hidden field to identify cluster type (human-readable string)
  telegramHandle?: string;
  requiredBy?: string; // YYYY-MM-DD format
}): Promise<boolean> => {
  try {
    const response = await fetch("/api/request-hardware-hubspot", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ data })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      // eslint-disable-next-line no-console
      console.error("Failed to submit to HubSpot:", errorData);
      return false;
    }

    const result = await response.json();
    // eslint-disable-next-line no-console
    console.log("Request submitted to HubSpot successfully:", result);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to submit request to HubSpot:", error);
    return false;
  }
};

export const findDynamicSupplyGpuById = (hardwareId: string, gpus: DynamicSupplyHardware[]) => {
  for (const f of gpus) {
    if (f.hardwareId === hardwareId) return f;
  }
  return undefined;
};

/** API `percent_completed` is elapsed time; UI "time remaining" bars need the inverse. */
export type ToPercentRemainingOptions = {
  computeMinutesRemaining?: number;
  /** Fallback when minute count is unavailable (e.g. cluster resources API). */
  remainingDuration?: string;
};

const isZeroRemainingDuration = (remainingDuration?: string) => {
  if (typeof remainingDuration !== "string") {
    return false;
  }

  return /^0\s*(hrs?|hours?)\s+0\s*(mins?|minutes?)$/i.test(remainingDuration.trim());
};

export const toPercentRemaining = (
  percentCompleted?: number,
  options?: ToPercentRemainingOptions
): number => {
  if (options?.computeMinutesRemaining === 0 || isZeroRemainingDuration(options?.remainingDuration)) {
    return 0;
  }

  if (typeof percentCompleted !== "number" || !Number.isFinite(percentCompleted)) {
    return 0;
  }

  return Math.min(100, Math.max(0, 100 - percentCompleted));
};
